import { defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client'
import { del, get, set } from 'idb-keyval'
import { forgetPhotos } from './photoCache'

const CACHE_KEY = 'mads.query-cache'

/**
 * Bumped when the shape of what is stored changes — a persisted cache written
 * by an older build is discarded rather than rehydrated into code that no
 * longer understands it. It is not a version of the app: a release that does
 * not change the wire format has no reason to throw away somebody's cellar.
 */
const CACHE_SCHEMA = 'v1'

/**
 * How long a persisted read is worth showing (issue #64). Long enough that a
 * fortnightly walk round the building still opens to a populated inventory,
 * short enough that nobody is reading last season's kit list off a tablet that
 * has been in a drawer.
 *
 * This is the *read* cache's bound and nothing else. How long an unsynced
 * queue of changes may sit before it is refused is a different number, and a
 * larger question — it is the project owner's to set, like the audit log's six
 * months, and it arrives with the queue.
 */
export const OFFLINE_CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000

/**
 * The queries that are worth having in a cellar, named rather than filtered out
 * (issue #64).
 *
 * An allowlist and not a denylist, deliberately: everything here is the
 * theatre's kit and the places it lives, which is what the walk needs and what
 * every signed-in account may read anyway. Accounts, audit logs, passkeys and
 * two-step status are none of those things, and writing them to a tablet's disk
 * to serve a screen nobody opens underground would be a privacy step down taken
 * by accident. A denylist gets that wrong the first time somebody adds a query
 * and forgets — this way the failure is a screen that says "Loading", not a
 * copy of the sign-in history sitting in IndexedDB.
 *
 * Both trashes are out for a plainer reason: nothing is restored from a store
 * cupboard.
 */
const PERSISTED_QUERIES = new Set([
  'dashboard',
  'items',
  'item',
  'locations',
  'fixtureModels',
  'autocomplete',
  'stocktakes',
  'stocktake',
])

/**
 * IndexedDB rather than `localStorage`: the inventory is bigger than the 5MB a
 * synchronous store gives you, and writing it is a main-thread stall on the
 * device least able to afford one.
 */
const persister = createAsyncStoragePersister({
  key: CACHE_KEY,
  // Written at most once a second. Every mutation invalidates half a dozen
  // queries (see `useInventoryInvalidator`), so an unthrottled persister would
  // rewrite the whole cache six times for one edit.
  throttleTime: 1000,
  storage: {
    getItem: (key) => get<string>(key).then((value) => value ?? null),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
})

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  maxAge: OFFLINE_CACHE_MAX_AGE,
  buster: CACHE_SCHEMA,
  dehydrateOptions: {
    // The default half of this is what keeps failures and half-finished fetches
    // out; the allowlist is what keeps everything else out.
    shouldDehydrateQuery: (query: Query) =>
      defaultShouldDehydrateQuery(query) && PERSISTED_QUERIES.has(String(query.queryKey[0])),
    // Nothing written is replayed from disk yet. A paused mutation restored
    // from a previous page load is a queue with none of the things a queue
    // needs — no client id, no base values, no conflict resolution — and
    // replaying one silently is the failure mode the whole design is built to
    // avoid. The queue arrives as its own thing.
    shouldDehydrateMutation: () => false,
  },
}

/**
 * Forgets the device's copy of the inventory.
 *
 * Called on sign-out, which is the only moment the app knows the person in
 * front of it may not be the person the data was fetched for. Kit and places
 * are readable by every account, so this is housekeeping rather than a
 * containment measure — but a tablet handed back to the box office should not
 * go on holding a walk-round's worth of the theatre's asset register.
 */
export async function forgetOfflineCache(): Promise<void> {
  // The photographs go with the records they belong to (issue #70). They are
  // kept under their own keys rather than inside the dehydrated cache — blobs
  // do not survive being serialised into it — so forgetting one has to forget
  // the other explicitly, or a tablet handed back to the box office would go on
  // holding pictures of the theatre's kit with nothing left to explain them.
  await Promise.all([del(CACHE_KEY), forgetPhotos()])
}
